import { lazy, Suspense, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { authApi } from '@/api/resources/auth'
import { type SsoResolution } from '@/api/schemas/session'
import { useSession } from '@/auth/SessionProvider'
import { defaultHomeFor } from '@/auth/roles'
import { FullPageSpinner } from '@/components/FullPageSpinner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { IS_MOCK } from '@/lib/env'
import { rememberWorkspaceSlug, rememberedWorkspaceSlug } from '@/lib/workspace'
import { AuthSplitShell, DarkAuthShell } from '../components/AuthShells'

/** Mock-mode-only helper card; the chunk never loads in test/prod. */
const DemoAccounts = lazy(() =>
  import('../components/DemoAccounts').then((module) => ({ default: module.DemoAccounts })),
)

function useCountdown(initialSeconds: number) {
  const [remaining, setRemaining] = useState(initialSeconds)
  useEffect(() => {
    if (remaining <= 0) return
    const timer = window.setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1_000)
    return () => window.clearInterval(timer)
  }, [remaining])
  return remaining
}

function formatCountdown(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/**
 * P1 login (Auth mockups 1a/1b): the workspace-resolution step of the hosted
 * flow. The SPA never sees a password — this page collects workspace + email,
 * resolves SSO on email blur (a resolved IdP swaps to the dark takeover
 * state), and Continue is a full-page navigation into the BFF chain
 * (GET /auth/login?tenantSlug=…&login_hint=…), where the themed Keycloak
 * page collects credentials. Lockout copy renders on ?locked= (mock seam).
 */
export function LoginPage() {
  const { user, isLoading } = useSession()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const returnTo = searchParams.get('returnTo')
  const lockedSeconds = Number(searchParams.get('locked') ?? 0)
  const lockCountdown = useCountdown(lockedSeconds)

  const [email, setEmail] = useState(searchParams.get('email') ?? '')
  // The BFF's /auth/login requires tenantSlug (per-tenant Keycloak realms).
  // Prefill from ?workspace= (post-signup handoff) or the remembered slug.
  const [workspace, setWorkspace] = useState(
    () => searchParams.get('workspace') ?? rememberedWorkspaceSlug(),
  )
  const [sso, setSso] = useState<Extract<SsoResolution, { ssoEnabled: true }> | null>(null)
  const [usePassword, setUsePassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const resolveSso = useMutation({
    mutationFn: authApi.resolveSso,
    meta: { silentError: true },
    onSuccess: (resolution) => {
      if (resolution.ssoEnabled) {
        setSso(resolution)
        // The email domain identified the workspace — adopt and remember it.
        if (resolution.tenantSlug) {
          setWorkspace(resolution.tenantSlug)
          rememberWorkspaceSlug(resolution.tenantSlug)
        }
      } else {
        setSso(null)
      }
    },
  })

  useEffect(() => {
    if (!isLoading && user) {
      void navigate(returnTo ?? defaultHomeFor(user), { replace: true })
    }
  }, [isLoading, user, returnTo, navigate])

  if (isLoading || user) return <FullPageSpinner />

  const startHostedLogin = () => {
    const tenantSlug = workspace.trim()
    if (!tenantSlug) return
    rememberWorkspaceSlug(tenantSlug)
    setSubmitting(true)
    window.location.assign(
      authApi.loginUrl(returnTo ?? undefined, { loginHint: email || undefined, tenantSlug }),
    )
  }

  /** SSO path: resolve returned a pre-built login URL — use it as-is, only
   *  carrying returnTo along so the callback lands where the user was going. */
  const startSsoLogin = (resolution: Extract<SsoResolution, { ssoEnabled: true }>) => {
    rememberWorkspaceSlug(resolution.tenantSlug)
    setSubmitting(true)
    try {
      const url = new URL(resolution.loginUrl, window.location.origin)
      if (returnTo && !url.searchParams.has('returnTo')) url.searchParams.set('returnTo', returnTo)
      window.location.assign(url.toString())
    } catch {
      window.location.assign(resolution.loginUrl)
    }
  }

  // SSO resolved → dark takeover state (mockup 1b)
  if (sso && !usePassword) {
    const domain = email.split('@')[1] ?? ''
    return (
      <DarkAuthShell showWordmark>
        <h1 className="mb-2 text-2xl font-semibold tracking-tight text-white">
          Continue with your company IdP
        </h1>
        <p className="mb-6 text-[13.5px] leading-relaxed text-[#94A3B8]">
          {domain} is managed by an identity provider.
        </p>
        <div className="mb-4 flex items-center gap-3 rounded-[9px] border border-white/10 bg-white/[0.03] px-3.5 py-3">
          <span className="flex size-[30px] items-center justify-center rounded-[7px] bg-brand font-mono text-xs font-bold text-accent-on-dark">
            {email.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-medium text-white">{email}</p>
            <p className="text-[11.5px] text-sidebar-muted">
              {sso.tenantSlug ?? domain} · {sso.idpDisplayName}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setSso(null)
              setUsePassword(false)
            }}
            className="text-[11.5px] font-medium text-accent-on-dark hover:underline"
          >
            Change
          </button>
        </div>
        <Button
          variant="accent"
          className="h-[46px] w-full text-[14.5px]"
          disabled={submitting}
          onClick={() => startSsoLogin(sso)}
        >
          {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
          Continue with {sso.idpDisplayName}
        </Button>
        <button
          type="button"
          onClick={() => setUsePassword(true)}
          className="mt-4 text-xs font-medium text-[#94A3B8] underline decoration-[#94A3B8]/40 hover:text-white"
        >
          Use password instead
        </button>
        <p className="mt-6 border-t border-white/10 pt-4 font-mono text-[11px] leading-relaxed text-[#6C7C94]">
          idpAlias: <span className="text-sidebar-fg">{sso.idpAlias}</span> · enforced by tenant SSO
          policy
        </p>
      </DarkAuthShell>
    )
  }

  return (
    <AuthSplitShell>
      <h1 className="mb-2 text-[30px] leading-tight font-semibold tracking-tight text-content">
        Sign in to FenceAuth
      </h1>
      <p className="mb-7 text-[14.5px] text-content-secondary">
        Auth &amp; billing infrastructure for your products.
      </p>

      {lockCountdown > 0 ? (
        <div
          role="alert"
          className="mb-5 flex items-center gap-2.5 rounded-[10px] border border-status-error-border bg-status-error-bg px-3.5 py-3 text-[12.5px] text-content-body"
        >
          <span className="size-2 shrink-0 rounded-full bg-status-error" aria-hidden />
          Too many attempts. Try again in{' '}
          <span className="font-mono font-semibold">{formatCountdown(lockCountdown)}</span> — or{' '}
          <Link to="/forgot-password" className="font-semibold text-status-error-fg underline">
            reset your password
          </Link>{' '}
          to clear the lock.
        </div>
      ) : null}

      <form
        className="flex flex-col gap-[18px]"
        onSubmit={(event) => {
          event.preventDefault()
          if (lockCountdown > 0) return
          startHostedLogin()
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="login-workspace">Workspace</Label>
          <Input
            id="login-workspace"
            autoComplete="organization"
            autoCapitalize="none"
            spellCheck={false}
            required
            placeholder="your-company"
            value={workspace}
            onChange={(event) => setWorkspace(event.target.value)}
            className="h-11 font-mono text-sm"
          />
          <p className="text-[11px] text-content-secondary">
            The workspace slug from signup — it&apos;s in your dashboard URL.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="login-email">Work email</Label>
          <div className="relative">
            <Input
              id="login-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                setSso(null)
                setUsePassword(false)
              }}
              onBlur={() => {
                if (email.includes('@')) resolveSso.mutate(email)
              }}
              className="h-11 text-sm"
            />
            {resolveSso.isPending ? (
              <Loader2
                className="absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin text-content-muted"
                aria-label="Checking sign-in options"
              />
            ) : null}
          </div>
        </div>

        <Button
          type="submit"
          className="h-[46px] text-[14.5px]"
          disabled={submitting || lockCountdown > 0}
        >
          {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
          Continue
        </Button>

        <p className="flex items-center justify-between text-[11.5px] text-content-secondary">
          <span>Next: enter your password on the secure sign-in page.</span>
          <Link to="/forgot-password" className="font-medium text-accent-strong hover:underline">
            Forgot password?
          </Link>
        </p>

        <div className="flex items-center gap-3" aria-hidden>
          <span className="h-px flex-1 bg-line" />
          <span className="text-[11px] font-medium tracking-widest text-content-muted uppercase">
            or
          </span>
          <span className="h-px flex-1 bg-line" />
        </div>

        <Button
          type="button"
          variant="outline"
          className="h-[46px] text-sm"
          disabled={submitting}
          onClick={() => {
            if (email.includes('@')) resolveSso.mutate(email)
          }}
        >
          <span className="inline-block size-[15px] rounded bg-brand" aria-hidden />
          Continue with SSO
        </Button>
      </form>

      {IS_MOCK ? (
        <Suspense fallback={null}>
          <div className="mt-6">
            <DemoAccounts
              onPick={(demoEmail) => {
                setEmail(demoEmail)
                setWorkspace('northwind')
                setSso(null)
                setUsePassword(false)
              }}
            />
          </div>
        </Suspense>
      ) : null}

      <div className="mt-8 flex items-center justify-between border-t border-line-subtle pt-5">
        <p className="text-[13px] text-content-secondary">
          No account?{' '}
          <Link to="/signup" className="font-semibold text-accent-strong hover:underline">
            Create a workspace
          </Link>
        </p>
        <span className="font-mono text-[11px] text-content-muted">fa_session · httpOnly</span>
      </div>
    </AuthSplitShell>
  )
}
